'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
// import { ChevronRight, Home } from "lucide-react"
import { useMemo, useEffect, useState } from 'react'

interface BreadcrumbItem {
  label: string
  href: string
  isCurrentPage?: boolean
}

interface BreadcrumbsProps {
  customItems?: BreadcrumbItem[]
  className?: string
}

export default function Breadcrumbs({ customItems, className = '' }: BreadcrumbsProps) {
  const pathname = usePathname()
  const [articleTitles, setArticleTitles] = useState<Record<string, string>>({})

  // Fetch article title when on an article page
  useEffect(() => {
    if (!pathname) return
    
    const segments = pathname.split('/').filter(Boolean)
    if (segments.length === 2 && segments[0] === 'artiklar') {
      const articleSlug = segments[1]
      
      if (!articleTitles[articleSlug]) {
        fetch(`/api/article-title?slug=${encodeURIComponent(articleSlug)}`)
          .then(res => res.json())
          .then(data => {
            if (data.title) {
              setArticleTitles(prev => ({
                ...prev,
                [articleSlug]: data.title
              }))
            }
          })
          .catch(error => {
            console.error('Error fetching article title:', error)
          })
      }
    }
  }, [pathname, articleTitles])

  const breadcrumbItems = useMemo(() => {
    try {
      // If custom items are provided, use them
      if (customItems && customItems.length > 0) {
        return [
          { label: 'Hem', href: '/' },
          ...customItems
        ]
      }

      // Auto-generate breadcrumbs from pathname
      if (!pathname) {
        return [{ label: 'Hem', href: '/' }]
      }
      
      const segments = pathname.split('/').filter(Boolean)
      const items: BreadcrumbItem[] = [{ label: 'Hem', href: '/' }]

      let currentPath = ''
      
      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i]
        currentPath += `/${segment}`
        const isLast = i === segments.length - 1

        // Handle specific routes
        if (segment === 'skola') {
          items.push({
            label: 'Hitta skola',
            href: '/skola',
            isCurrentPage: isLast
          })
        } else if (segment === 'aktivitet') {
          items.push({
            label: 'Hitta aktivitet',
            href: '/aktivitet',
            isCurrentPage: isLast
          })
        } else if (segment === 'artiklar') {
          items.push({
            label: 'Artiklar',
            href: '/artiklar',
            isCurrentPage: isLast
          })
        } else if (segment === 'om-oss') {
          items.push({
            label: 'Om oss',
            href: '/om-oss',
            isCurrentPage: isLast
          })
        } else if (segment === 'mina-sidor') {
          items.push({
            label: 'Mina sidor',
            href: '/mina-sidor',
            isCurrentPage: isLast
          })
        } else if (segment === 'enhet' && segments[i - 1] === 'skola') {
          // School unit page - don't add "enhet" to breadcrumb
          continue
        } else if (segment === 'kommun' && segments[i - 1] === 'skola') {
          // Kommun page - don't add "kommun" to breadcrumb
          continue
        } else if (segments[i - 1] === 'artiklar') {
          // Article page - use fetched title or fallback to formatted slug
          const articleTitle = articleTitles[segment] || decodeURIComponent(segment)
            .replace(/-/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
          items.push({
            label: articleTitle,
            href: currentPath,
            isCurrentPage: isLast
          })
        } else if (segments[i - 1] === 'enhet') {
          // School detail page - will be handled by custom breadcrumbs
          items.push({
            label: 'Skola',
            href: currentPath,
            isCurrentPage: isLast
          })
        } else if (segments[i - 1] === 'kommun') {
          // Kommun page - format the kommun name
          const kommunName = decodeURIComponent(segment)
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
          items.push({
            label: kommunName,
            href: currentPath,
            isCurrentPage: isLast
          })
        } else if (segments[i - 1] === 'skola' && segment !== 'enhet' && segment !== 'kommun') {
          // Area page - format the area name
          const areaName = decodeURIComponent(segment)
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
          items.push({
            label: areaName,
            href: currentPath,
            isCurrentPage: isLast
          })
        } else {
          // Default formatting
          const label = decodeURIComponent(segment)
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
          items.push({
            label,
            href: currentPath,
            isCurrentPage: isLast
          })
        }
      }

      return items
    } catch (error) {
      console.error('Error generating breadcrumbs:', error)
      // Fallback to basic breadcrumb
      return [{ label: 'Hem', href: '/' }]
    }
  }, [pathname, customItems, articleTitles])

  // Generate JSON-LD structured data for SEO
  const structuredData = useMemo(() => {
    try {
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': breadcrumbItems.map((item, index) => ({
          '@type': 'ListItem',
          'position': index + 1,
          'name': item.label,
          'item': `https://valjratt.se${item.href}`
        }))
      }
    } catch (error) {
      console.error('Error generating breadcrumb structured data:', error)
      return null
    }
  }, [breadcrumbItems])

  // Don't render breadcrumbs on home page or if there's only one item
  if (!pathname || pathname === '/' || breadcrumbItems.length <= 1) {
    return null
  }

  return (
    <>
      {/* JSON-LD Structured Data */}
      {structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      )}
      
      {/* Breadcrumb Navigation */}
      <nav 
        aria-label="Breadcrumb" 
        className={`bg-gray-50 border-b border-gray-200 ${className}`}
        role="navigation"
      >
        <div className="container mx-auto px-4 py-3">
          <ol 
            className="flex flex-wrap items-center space-x-1 text-sm text-gray-600"
            itemScope 
            itemType="https://schema.org/BreadcrumbList"
          >
            {breadcrumbItems.map((item, index) => (
              <li 
                key={`${item.href}-${index}`}
                className="flex items-center"
                itemProp="itemListElement"
                itemScope
                itemType="https://schema.org/ListItem"
              >
                {index > 0 && (
                  <span 
                    className="mx-2 text-gray-400" 
                    aria-hidden="true"
                  >
                    &gt;
                  </span>
                )}
                
                {item.isCurrentPage ? (
                  <span 
                    className="font-medium text-gray-900"
                    aria-current="page"
                    itemProp="name"
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link
                    href={item.href}
                    className="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200"
                    itemProp="item"
                  >
                    <span itemProp="name">
                      {item.label}
                    </span>
                  </Link>
                )}
                
                {/* Hidden schema.org properties */}
                <meta itemProp="position" content={String(index + 1)} />
              </li>
            ))}
          </ol>
        </div>
      </nav>
    </>
  )
} 